import { useState, useCallback } from 'react';
import { playbookApi } from '../api/playbookApi';
import type { PlaybookDiff, VersionHistoryEntry } from '../types/playbook.types';

export function useEditorDiff(playbookId: string, slug?: string) {
  const [diffMode, setDiffMode] = useState(false);
  const [diff, setDiff] = useState<PlaybookDiff | null>(null);
  const [versions, setVersions] = useState<VersionHistoryEntry[]>([]);
  const [baseVersionId, setBaseVersionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const loadVersions = useCallback(async () => {
    if (!slug) return;
    try {
      const resp = await playbookApi.getVersionHistory(slug);
      const data = (resp as any)?.versions ?? (resp as any)?.data ?? resp;
      setVersions(Array.isArray(data) ? data : []);
    } catch {
      // silent
    }
  }, [slug]);

  const loadDiff = useCallback(async (targetId: string) => {
    setLoading(true);
    try {
      const resp = await playbookApi.getPlaybookDiff(targetId, playbookId);
      const data = (resp as any)?.diff ?? (resp as any)?.data ?? resp;
      setDiff(data as PlaybookDiff);
      setBaseVersionId(targetId);
    } catch {
      setDiff(null);
    } finally {
      setLoading(false);
    }
  }, [playbookId]);

  const enterDiffMode = useCallback(async (baseId?: string) => {
    await loadVersions();
    if (baseId) {
      await loadDiff(baseId);
    }
    setDiffMode(true);
  }, [loadVersions, loadDiff]);

  const exitDiffMode = useCallback(() => {
    setDiffMode(false);
    setDiff(null);
    setBaseVersionId(null);
  }, []);

  return {
    diffMode,
    diff,
    versions,
    baseVersionId,
    loading,
    enterDiffMode,
    exitDiffMode,
    loadDiff,
    setBaseVersionId,
  };
}
